import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  FileText, 
  Users, 
  Settings, 
  LogOut, 
  Menu,
  X,
  Plus,
  Building2,
  ChevronDown
} from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { RealtimeNotifications } from './RealtimeNotifications';

// Desktop Sidebar Component
export const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, empresaActual, logout, cambiarEmpresa, hasPermission } = useAuth();

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/presupuestos', label: 'Presupuestos', icon: FileText },
    { path: '/clientes', label: 'Clientes', icon: Users },
    { path: '/configuracion', label: 'Configuración', icon: Settings, roles: ['OWNER'] },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <aside className="hidden lg:flex w-64 bg-white border-r border-slate-200 min-h-screen flex-col">
      {/* Logo */}
      <div className="p-6 border-b border-slate-200">
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-blue-600 rounded-md flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            OkPresu
          </span>
        </Link>
      </div>

      {/* Empresa selector */}
      {user?.empresas && user.empresas.length > 0 && (
        <div className="p-4 border-b border-slate-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full justify-between">
                <div className="flex items-center gap-2 truncate">
                  <Building2 className="w-4 h-4 shrink-0" />
                  <span className="truncate">{empresaActual?.nombre_comercial || 'Seleccionar'}</span>
                </div>
                <ChevronDown className="w-4 h-4 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56">
              {user.empresas.map((empresa) => (
                <DropdownMenuItem 
                  key={empresa.id} 
                  onClick={() => cambiarEmpresa(empresa)}
                  className={empresa.id === empresaActual?.id ? 'bg-slate-100' : ''}
                >
                  <div className="flex flex-col">
                    <span>{empresa.nombre_comercial}</span>
                    <span className="text-xs text-slate-500">{empresa.rol}</span>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* CTA Button */}
      <div className="p-4">
        <Button 
          className="w-full bg-blue-600 hover:bg-blue-700"
          onClick={() => navigate('/presupuestos/nuevo')}
          data-testid="sidebar-new-quote-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Presupuesto
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4">
        <ul className="space-y-1">
          {navItems.map((item) => {
            if (item.roles && !hasPermission(item.roles)) return null;
            
            const isActive = location.pathname === item.path || 
                           (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
            
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`sidebar-link ${isActive ? 'active' : ''}`}
                  data-testid={`sidebar-nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User menu */}
      <div className="p-4 border-t border-slate-200">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="w-full justify-start">
              <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center mr-2">
                <span className="text-sm font-medium text-slate-600">
                  {user?.nombre?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex flex-col items-start">
                <span className="text-sm font-medium">{user?.nombre}</span>
                <span className="text-xs text-slate-500">{user?.email}</span>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuItem onClick={() => navigate('/configuracion')}>
              <Settings className="w-4 h-4 mr-2" />
              Configuración
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLogout} className="text-red-600">
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        
        {/* Real-time Notifications for Desktop */}
        <div className="px-4 pb-2">
          <RealtimeNotifications token={localStorage.getItem('token')} />
        </div>
      </div>
    </aside>
  );
};

// Mobile Header Component
const MobileHeader = ({ onMenuOpen }) => {
  const navigate = useNavigate();
  const { empresaActual } = useAuth();
  const token = localStorage.getItem('token');

  return (
    <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-200 safe-area-top">
      <div className="flex items-center justify-between px-4 h-14">
        <button onClick={onMenuOpen} className="p-2 -ml-2">
          <Menu className="w-6 h-6 text-slate-700" />
        </button>
        
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-blue-600 rounded-md flex items-center justify-center">
            <FileText className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-slate-900 text-sm truncate max-w-[150px]">
            {empresaActual?.nombre_comercial || 'OkPresu'}
          </span>
        </div>

        <div className="flex items-center gap-1">
          <RealtimeNotifications token={token} />
          <Button 
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 h-8 px-3"
            onClick={() => navigate('/presupuestos/nuevo')}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </header>
  );
};

// Mobile Navigation Drawer
const MobileDrawer = ({ open, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, empresaActual, logout, cambiarEmpresa, hasPermission } = useAuth();

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/presupuestos', label: 'Presupuestos', icon: FileText },
    { path: '/clientes', label: 'Clientes', icon: Users },
    { path: '/configuracion', label: 'Configuración', icon: Settings, roles: ['OWNER'] },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    onClose();
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="left" className="w-[280px] p-0">
        {/* Header */}
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 rounded-md flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              OkPresu
            </span>
          </div>
        </div>

        {/* User info */}
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-white">
                {user?.nombre?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="font-medium text-slate-900">{user?.nombre}</p>
              <p className="text-sm text-slate-500">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Empresa selector */}
        {user?.empresas && user.empresas.length > 1 && (
          <div className="p-4 border-b border-slate-200">
            <p className="text-xs text-slate-500 mb-2">Empresa</p>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full justify-between">
                  <span className="truncate">{empresaActual?.nombre_comercial}</span>
                  <ChevronDown className="w-4 h-4 shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                {user.empresas.map((empresa) => (
                  <DropdownMenuItem 
                    key={empresa.id} 
                    onClick={() => cambiarEmpresa(empresa)}
                  >
                    {empresa.nombre_comercial}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {/* Navigation */}
        <nav className="p-4">
          <ul className="space-y-1">
            {navItems.map((item) => {
              if (item.roles && !hasPermission(item.roles)) return null;
              
              const isActive = location.pathname === item.path || 
                             (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
              
              return (
                <li key={item.path}>
                  <button
                    onClick={() => handleNavigation(item.path)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      isActive 
                        ? 'bg-blue-50 text-blue-700 font-medium' 
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-white">
          <Button 
            variant="ghost" 
            className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={handleLogout}
          >
            <LogOut className="w-5 h-5 mr-3" />
            Cerrar sesión
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Bottom Navigation for Mobile
const BottomNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const navItems = [
    { path: '/dashboard', label: 'Inicio', icon: LayoutDashboard },
    { path: '/presupuestos', label: 'Presupuestos', icon: FileText },
    { path: '/clientes', label: 'Clientes', icon: Users },
    { path: '/configuracion', label: 'Ajustes', icon: Settings, roles: ['OWNER'] },
  ];

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 safe-area-bottom">
      <div className="flex items-center justify-around h-16">
        {navItems.map((item) => {
          if (item.roles && !hasPermission(item.roles)) return null;
          
          const isActive = location.pathname === item.path || 
                         (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
          
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                isActive ? 'text-blue-600' : 'text-slate-500'
              }`}
            >
              <item.icon className={`w-5 h-5 ${isActive ? 'stroke-[2.5px]' : ''}`} />
              <span className="text-[10px] mt-1 font-medium">{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

// Main Dashboard Layout (Responsive)
export const DashboardLayout = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <Sidebar />
      
      {/* Mobile Header */}
      <MobileHeader onMenuOpen={() => setDrawerOpen(true)} />
      
      {/* Mobile Drawer */}
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      
      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Add padding for mobile header and bottom nav */}
        <div className="lg:p-8 p-4 pt-[72px] pb-20 lg:pt-8 lg:pb-8">
          {children}
        </div>
      </main>
      
      {/* Mobile Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
};
